import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { supabase } from "../lib/supabase";

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser]               = useState(null);
  const [profile, setProfile]         = useState(null);
  const [loading, setLoading]         = useState(true);   // initial session check
  const [profileLoading, setProfileLoading] = useState(false);

  // Ref so fetchProfile never becomes a stale closure and never
  // triggers useEffect re-runs when used as a dependency.
  const fetchingRef = useRef(false); // guard against concurrent fetches

  // ── Core fetch — plain async function, not a state-setter loop ──────────
  const fetchProfile = async (userId) => {
    // Prevent parallel fetches for the same user
    if (fetchingRef.current) return null;
    fetchingRef.current = true;
    setProfileLoading(true);

    try {
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("id", userId)
        .single();

      if (!error && data) {
        setProfile(data);
        return data;
      }
      return null;
    } catch {
      return null;
    } finally {
      fetchingRef.current = false;
      setProfileLoading(false);
    }
  };

  // ── Bootstrap: run ONCE on mount ────────────────────────────────────────
  useEffect(() => {
    let mounted = true;

    const bootstrap = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (!mounted) return;

      if (session?.user) {
        setUser(session.user);
        await fetchProfile(session.user.id);
      }

      if (mounted) setLoading(false);
    };

    bootstrap();

    // ── Auth state listener ────────────────────────────────────────────────
    // Only handles SIGN_IN and SIGN_OUT — does NOT re-fetch profile on every
    // TOKEN_REFRESHED event (that was the infinite-loop trigger).
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        if (event === "SIGNED_IN" && session?.user) {
          setUser(session.user);
          // Only fetch if we don't already have this user's profile
          setProfile((prev) => {
            if (prev?.id !== session.user.id) {
              fetchProfile(session.user.id);
            }
            return prev;
          });
        }

        if (event === "SIGNED_OUT") {
          setUser(null);
          setProfile(null);
          setLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []); // ← empty array: runs once only

  // ── Auth actions ─────────────────────────────────────────────────────────
  const signUp = async ({ email, password, name, phone, role = "user" }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name, role } },
    });
    if (error) throw error;

    if (data.user && phone) {
      await supabase.from("users").update({ phone }).eq("id", data.user.id);
    }
    return data;
  };

  // Returns { user, session, profile } so LoginPage can redirect immediately
  const signIn = async ({ email, password }) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;

    const profileData = await fetchProfile(data.user.id);
    return { ...data, profile: profileData };
  };

  const signOut = async () => {
    // Clear local state first so UI responds immediately
    setUser(null);
    setProfile(null);
    await supabase.auth.signOut();
  };

  // ── Derived flags ─────────────────────────────────────────────────────────
  const isAdmin      = profile?.role === "admin";
  const isRestaurant = profile?.role === "restaurant";
  const isUser       = profile?.role === "user";

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        profileLoading,
        isAdmin,
        isRestaurant,
        isUser,
        signUp,
        signIn,
        signOut,
        fetchProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
